var _excluded = ["disabled", "edges", "fullWidth", "helperText", "helperTextPlacement", "label", "placeholder", "popupIcon", "required", "size", "variant", "showError", "errorMessage", "name", "inputRef", "hideLabel", "disableIconRotation", "TextInputProps", "onChange"],
  _excluded2 = ["InputProps"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import MuiAutocomplete from '@material-ui/core/Autocomplete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import FormControl from '../FormControl';
import { isEmpty } from '../../utils/helpers';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      '& .MuiAutocomplete-popupIndicatorOpen': {
        transform: function transform(_ref) {
          var disableIconRotation = _ref.disableIconRotation;
          return disableIconRotation ? 'rotate(0)' : null;
        }
      }
    },
    input: {
      margin: 0,
      '&& .MuiInputBase-root': {
        width: '100%',
        padding: function padding(_ref2) {
          var size = _ref2.size;
          return size === 'small' ? "" + theme.spacing(2, 8, 2, 2) : '12px 16px';
        },
        border: function border(_ref3) {
          var variant = _ref3.variant;
          return variant === 'filled' ? "2px solid transparent" : null;
        },
        borderRadius: function borderRadius(_ref4) {
          var edges = _ref4.edges;
          switch (edges) {
            case 'rounded':
              return 99;
            case 'sharp':
              return 0;
            default:
              return theme.shape.borderRadius;
          }
        }
      },
      '& .MuiFilledInput-root': {
        backgroundColor: theme.palette.grey.transparent
      },
      '& .Mui-focused': {
        border: function border(_ref5) {
          var variant = _ref5.variant,
            required = _ref5.required;
          return variant === 'filled' ? "2px solid " + (required ? theme.palette.secondary.main : theme.palette.primary.main) : null;
        }
      },
      '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: function borderColor(_ref6) {
          var required = _ref6.required,
            selectedValue = _ref6.selectedValue;
          return required && isEmpty(selectedValue) ? theme.palette.secondary.main : null;
        }
      },
      '& input': {
        padding: '0 !important'
      },
      '& .MuiTouchRipple-root': {
        display: 'none'
      }
    }
  };
});
function Autocomplete(_ref7) {
  var disabled = _ref7.disabled,
    edges = _ref7.edges,
    fullWidth = _ref7.fullWidth,
    helperText = _ref7.helperText,
    helperTextPlacement = _ref7.helperTextPlacement,
    label = _ref7.label,
    placeholder = _ref7.placeholder,
    popupIcon = _ref7.popupIcon,
    required = _ref7.required,
    size = _ref7.size,
    variant = _ref7.variant,
    showError = _ref7.showError,
    errorMessage = _ref7.errorMessage,
    name = _ref7.name,
    inputRef = _ref7.inputRef,
    hideLabel = _ref7.hideLabel,
    disableIconRotation = _ref7.disableIconRotation,
    TextInputProps = _ref7.TextInputProps,
    onChange = _ref7.onChange,
    rest = _objectWithoutPropertiesLoose(_ref7, _excluded);
  var _useState = useState(rest.value || null),
    selectedValue = _useState[0],
    setSelectedValue = _useState[1];
  var classes = useStyles({
    variant: variant,
    size: size,
    edges: edges,
    required: required,
    disableIconRotation: disableIconRotation,
    selectedValue: selectedValue
  });
  var handleChange = function handleChange(event, val) {
    setSelectedValue(val);
    for (var _len = arguments.length, extras = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      extras[_key - 2] = arguments[_key];
    }
    onChange.apply(void 0, [event, val].concat(extras));
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    name: name,
    fullWidth: fullWidth,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    hideLabel: hideLabel,
    edges: edges
  }, /*#__PURE__*/React.createElement(MuiAutocomplete, _extends({
    className: classes.root,
    disabled: disabled,
    popupIcon: popupIcon === undefined ? /*#__PURE__*/React.createElement(KeyboardArrowDownIcon, null) : popupIcon,
    renderInput: function renderInput(_ref8) {
      var InputProps = _ref8.InputProps,
        params = _objectWithoutPropertiesLoose(_ref8, _excluded2);
      return /*#__PURE__*/React.createElement(TextField, _extends({
        name: name,
        className: classes.input,
        variant: variant,
        placeholder: placeholder
      }, params, {
        InputProps: _extends({
          autoComplete: 'off'
        }, InputProps, TextInputProps),
        error: showError && errorMessage,
        inputRef: inputRef || null
      }));
    },
    onChange: handleChange
  }, rest)));
}
Autocomplete.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Prevent icon rotating on open
   */
  disableIconRotation: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * Field name.
   */
  name: PropTypes.string.isRequired,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * The icon that displays the arrow.
   */
  popupIcon: PropTypes.any,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * The size to use.
   */
  size: PropTypes.oneOf(['medium', 'small']),
  /**
   * Props to pass to the TextField component.
   */
  TextInputProps: PropTypes.object,
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['filled', 'outlined']),
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * @ignore
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  /**
   * Make label visible only to screen readers
   */
  hideLabel: PropTypes.bool,
  /**
   * Function to handle value chage
   */
  onChange: PropTypes.func
} : {};
Autocomplete.defaultProps = {
  disabled: false,
  disableIconRotation: false,
  edges: 'normal',
  errorMessage: '',
  fullWidth: true,
  helperText: '',
  helperTextPlacement: 'above',
  hideLabel: false,
  inputRef: null,
  label: '',
  placeholder: '',
  popupIcon: undefined,
  required: false,
  showError: false,
  size: 'medium',
  TextInputProps: {},
  variant: 'outlined',
  onChange: function onChange() {}
};
export default Autocomplete;